import type { Organization } from '@/types/organization';

type ClinicStatusLike = Pick<Organization, 'status' | 'terminatedOn' | 'goLiveOn' | 'onboardingOn'>;

/**
 * Same rule as the ops portal (`inferStatus` in ops-portal-2.0 synergyOrgs.ts): the lifecycle
 * `status` decides; with no status, a terminated date means inactive and an onboarding date with
 * no go-live means onboarding. Anything else counts as active.
 */
export function isActiveClinic(clinic: Partial<ClinicStatusLike>): boolean {
  const status = (clinic.status ?? '').trim().toLowerCase();
  if (status) return status === 'active';
  if (clinic.terminatedOn) return false;
  if (clinic.onboardingOn && !clinic.goLiveOn) return false;
  return true;
}

export interface ClinicScope<T> {
  /** Active clinics the user can add a patient to. */
  all: T[];
  /** Active clinics in the page's clinic filter; empty when no filter narrows the list. */
  filtered: T[];
}

/**
 * The page's clinic filter is `userSettings.filteredClinics` (the global filter the patient list
 * reads). It narrows the picker only when it is a real subset of the user's clinics.
 */
export function clinicScope<T extends { id: string } & Partial<ClinicStatusLike>>(
  clinics: T[] | null | undefined,
  filteredClinics: { id: string }[] | null | undefined
): ClinicScope<T> {
  const all = (clinics ?? []).filter(isActiveClinic);
  const ids = new Set((filteredClinics ?? []).map((clinic) => clinic.id));
  const filtered = ids.size ? all.filter((clinic) => ids.has(clinic.id)) : [];
  return {
    all,
    filtered: filtered.length > 0 && filtered.length < all.length ? filtered : []
  };
}
